import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Table,
  Badge,
  Row,
  Col,
  Alert,
  OverlayTrigger,
  Tooltip,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  getDoc,
  getFirestore,
  updateDoc,
} from "firebase/firestore";
import {
  calcularMesesComputables,
  calcularGratificacion,
  calcularCTS,
  calcularEssalud,
  calcularRenta5ta,
  calcularDescuentosPorFaltas,
  calcularDescuentosPorTardanzas,
  calcularBonificacionesConstruccionCivil,
  calcularSistemaPensionario,
  calcularEssaludVida,
  calcularSCTR,
  calcularVidaLey,
  calcularLiquidacion,
} from "../../utils/calculosLaborales";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCalculator,
  FaSave,
  FaFileExcel,
  FaCalendarAlt,
  FaBriefcase,
  FaUsers,
  FaSearch,
  FaArrowLeft,
  FaCheckCircle,
  FaDatabase,
  FaMoneyBillWave,
  FaChartLine,
  FaSync,
  FaPlus,
  FaTimes,
  FaChartBar,
  FaClipboardList,
  FaInfoCircle,
} from "react-icons/fa";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import * as XLSX from "xlsx"; // Agrega esta importación
import { logBusinessAction } from "../../services/AuditService";
import PlanillaTable from "./PlanillaTable";
import ModalAjustesManuales from "./components/ModalAjustesManuales";
import {
  getUltimoDiaMes,
  getDescripcionRegimen,
  obtenerDatosAsistencia,
  mostrarError,
  mostrarAlerta,
  exportarAExcel,
  calcularBonos,
  construirDetalleEmpleado,
  calcularPlanillaMensual,
  calcularPlanillaGratificacion,
  calcularPlanillaCTS,
  calcularPlanillaLiquidacion,
  marcarAdelantosComoAplicados,
} from "../../utils/planillaHelpers";

const db = getFirestore(firebaseApp);

// Configuración de regímenes laborales
const configuracionRegimen = {
  parametros: {
    GENERAL: {
      gratificacion: { aplica: true, factor: 1, incluye_bonos_remunerativos: false },
      cts: { aplica: true, factor: 1 },
      vacaciones: { dias: 30 },
      asignacion_familiar: { aplica: true },
      essalud: { porcentaje: 9 },
      indemnizacion: { factor_por_anio: 1.5, tope_anios: 12 }
    },
    MYPE_MICRO: {
      gratificacion: { aplica: false, factor: 0 },
      cts: { aplica: false, factor: 0 },
      vacaciones: { dias: 15 },
      asignacion_familiar: { aplica: false },
      essalud: { porcentaje: 9 },
      indemnizacion: { factor_por_anio: 0.33, tope_dias: 90 }
    },
    MYPE_PEQUENA: {
      gratificacion: { aplica: true, factor: 0.5, incluye_bonos_remunerativos: false },
      cts: { aplica: true, factor: 0.5 },
      vacaciones: { dias: 15 },
      asignacion_familiar: { aplica: false },
      essalud: { porcentaje: 9 },
      indemnizacion: { factor_por_anio: 0.66, tope_dias: 120 }
    },
    AGRARIO: {
      gratificacion: { aplica: false, incluida_en_diario: true },
      cts: { aplica: false, incluida_en_diario: true },
      vacaciones: { dias_min: 15, dias_max: 30, escala_progresiva: true },
      asignacion_familiar: { aplica: true },
      essalud: { porcentaje: 9 },
      indemnizacion: { factor_por_anio: 1.5, tope_anios: 12 }
    },
    CONSTRUCCION_CIVIL: {
      gratificacion: { aplica: true, factor: 0.5, incluye_bonos_remunerativos: false },
      cts: { aplica: true, factor: 0.5 },
      vacaciones: { dias: 15 },
      asignacion_familiar: { aplica: false },
      essalud: { porcentaje: 9 },
      indemnizacion: { factor_por_anio: 1.5, tope_anios: 12 }
    }
  }
};

// Función de validación general para parámetros críticos
const validarParametrosCalculo = (tipoPlanilla, periodo, empleadosSeleccionados, config) => {
  const errores = [];

  if (!tipoPlanilla || !['MENSUAL', 'GRATIFICACION', 'CTS', 'LIQUIDACION'].includes(tipoPlanilla)) {
    errores.push('Tipo de planilla inválido');
  }

  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) {
    errores.push('Periodo debe estar en formato YYYY-MM');
  }

  if (!Array.isArray(empleadosSeleccionados) || empleadosSeleccionados.length === 0) {
    errores.push('Debe seleccionar al menos un empleado');
  }

  if (!config || typeof config !== 'object') {
    errores.push('Configuración de planilla no cargada');
  } else {
    // Validar campos críticos de configuración
    if (!config.uit || typeof config.uit !== 'number' || config.uit <= 0) {
      errores.push('UIT no configurado correctamente');
    }
    if (!config.sueldo_minimo || typeof config.sueldo_minimo !== 'number' || config.sueldo_minimo <= 0) {
      errores.push('Sueldo mínimo no configurado correctamente');
    }
  }

  return errores;
};

// Función mejorada para mostrar errores
const mostrarErroresValidacion = (errores) => {
  if (errores.length > 0) {
    const mensaje = errores.join('\n• ');
    Swal.fire({
      icon: 'error',
      title: 'Errores de Validación',
      html: `<div style="text-align: left;"><strong>Por favor corrija los siguientes errores:</strong><br>• ${mensaje}</div>`,
      confirmButtonText: 'Entendido'
    });
    return true; // Hay errores
  }
  return false; // No hay errores
};

const ProcesoPlanilla = ({ usuario }) => {
  const { id } = useParams();
  const [planillaExistente, setPlanillaExistente] = useState(null);
  const [periodo, setPeriodo] = useState("");
  const [tipoPlanilla, setTipoPlanilla] = useState("MENSUAL");
  const [empleados, setEmpleados] = useState([]);
  const [empleadosSeleccionados, setEmpleadosSeleccionados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [resultados, setResultados] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [config, setConfig] = useState({});
  const [regimenEmpresa, setRegimenEmpresa] = useState("GENERAL");
  const [configLoaded, setConfigLoaded] = useState(false);
  const [bonosMap, setBonosMap] = useState({});

  // Estados para liquidación
  const [fechaCese, setFechaCese] = useState("");
  const [tipoCese, setTipoCese] = useState("despido");

  // Estados para Ajustes Manuales (Contadores)
  const [ajustesData, setAjustesData] = useState({});
  const [showModalAjustes, setShowModalAjustes] = useState(false);
  const [empleadoSeleccionadoAjustes, setEmpleadoSeleccionadoAjustes] = useState(null);

  const handleAbrirAjustes = (detalle) => {
    setEmpleadoSeleccionadoAjustes(detalle);
    setShowModalAjustes(true);
  };

  const handleGuardarAjustes = (empleadoId, nuevosAjustes) => {
    setAjustesData(prev => ({
      ...prev,
      [empleadoId]: nuevosAjustes
    }));
  };

  // Re-calcular automáticamente cuando cambien los ajustes o la selección de empleados
  useEffect(() => {
    if (resultados) {
      console.log("♻️ Re-calculando por cambios en ajustes...");
      calcularPlanilla(true); // Flag para preservar ajustes
    }
  }, [ajustesData]);

  useEffect(() => {
    if (config && config.regimen_empresa) {
      setRegimenEmpresa(config.regimen_empresa);
    }
  }, [config]);

  useEffect(() => {
    const cargarDatos = async () => {
      await cargarEmpleados();
      await cargarConfiguracion();
      await cargarBonos();
    };
    cargarDatos();
  }, [usuario.ruc]);

  useEffect(() => {
    const cargarPlanillaExistente = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setEmpleadosSeleccionados([]);
        const planillaRef = doc(db, "pl_planillas", id);
        const planillaDoc = await getDoc(planillaRef);
        if (planillaDoc.exists()) {
          const planillaData = planillaDoc.data();
          setPlanillaExistente(planillaData);
          setPeriodo(planillaData.periodo);
          setTipoPlanilla(planillaData.tipo_planilla);
          const q = query(collection(db, "pl_planilla_detalle"), where("planilla_id", "==", id));
          const detalleSnapshot = await getDocs(q);
          const detalleData = [];
          detalleSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.empleado_id && data.nombres) {
              detalleData.push(data);
            }
          });
          setResultados({ ...planillaData, detalle: detalleData });
        }
      } catch (error) {
        console.error("Error al cargar planilla:", error);
        mostrarError("Error al cargar la planilla");
      } finally {
        setLoading(false);
      }
    };
    cargarPlanillaExistente();
  }, [id]);

  const cargarBonos = async () => {
    try {
      const q = query(
        collection(db, "pl_bonos"),
        where("ruc", "==", usuario.ruc),
        where("estado", "==", "activo") // Añadir filtro por estado activo
      );
      const snapshot = await getDocs(q);
      const bonos = {};

      snapshot.forEach((doc) => {
        bonos[doc.id] = doc.data();
      });

      setBonosMap(bonos);
    } catch (error) {
      console.error("Error al cargar bonos:", error);
    }
  }

  const cargarConfiguracion = async () => {
    try {
      // Cargar configuración específica del RUC del administrador desde pl_config/{ruc}
      const configRef = doc(db, "pl_config", usuario.ruc);
      const docSnap = await getDoc(configRef);

      if (!docSnap.exists()) {
        mostrarError(`No se encontró configuración de planilla para RUC ${usuario.ruc} en Firestore`);
        return;
      }

      let configuracionCompleta = docSnap.data();

      // Inyectar valores por defecto si faltan parámetros críticos
      if (!configuracionCompleta.uit) configuracionCompleta.uit = 5500;
      if (!configuracionCompleta.sueldo_minimo) configuracionCompleta.sueldo_minimo = 1130;

      // Calcular asignación familiar dinámicamente: 10% del sueldo mínimo (RMV)
      const sueldoMinimo = configuracionCompleta.sueldo_minimo;
      configuracionCompleta.asignacion_familiar = sueldoMinimo * 0.1;

      // Cargar regímenes laborales específicos del RUC desde pl_regimenes_laborales/{ruc}
      const regimenesRef = doc(db, "pl_regimenes_laborales", usuario.ruc);
      const regimenesSnap = await getDoc(regimenesRef);
      if (regimenesSnap.exists()) {
        configuracionCompleta.regimenes = regimenesSnap.data().parametros;
      }

      console.log("⚙️ [ProcesoPlanilla] Configuración cargada:", configuracionCompleta);
      setConfig(configuracionCompleta);
      setConfigLoaded(true);
    } catch (error) {
      console.error("Error al cargar configuración:", error);
      mostrarError("Error al cargar la configuración de planilla");
    }
  };

  const resetearRetencionesAnuales = async () => {
    const mesActual = new Date().getMonth() + 1;
    if (mesActual !== 1) return;
    try {
      const q = query(collection(db, "pl_empleados"), where("ruc_empresa", "==", usuario.ruc), where("activo", "==", true));
      const querySnapshot = await getDocs(q);
      const updates = querySnapshot.docs.map((doc) => updateDoc(doc.ref, { retenciones_acumuladas: 0 }));
      await Promise.all(updates);
    } catch (error) {
      console.error("Error al resetear retenciones acumuladas:", error);
    }
  };

  const cargarEmpleados = async () => {
    try {
      await resetearRetencionesAnuales();
      const q = query(collection(db, "pl_empleados"), where("ruc_empresa", "==", usuario.ruc), where("activo", "==", true));
      const querySnapshot = await getDocs(q);
      const empleadosData = [];
      querySnapshot.forEach((doc) => {
        empleadosData.push({ id: doc.id, ...doc.data() });
      });
      setEmpleados(empleadosData);
    } catch (error) {
      console.error("Error al cargar empleados:", error);
      mostrarError("Error al cargar empleados");
    }
  };

  const toggleEmpleado = (empleadoId) => {
    setEmpleadosSeleccionados((prev) => {
      if (prev.includes(empleadoId)) {
        return prev.filter((id) => id !== empleadoId);
      } else {
        return [...prev, empleadoId];
      }
    });
  };

  const toggleTodos = () => {
    if (empleadosSeleccionados.length === empleados.length) {
      setEmpleadosSeleccionados([]);
    } else {
      setEmpleadosSeleccionados(empleados.map((e) => e.id));
    }
  };

  const calcularPlanilla = async (preservarAjustes = false) => {
    if (!preservarAjustes) {
       // Si es un cálculo fresco desde el botón, podrías querer limpiar ajustes? 
       // Generalmente es mejor mantenerlos si el usuario ya trabajó en ellos.
    }
    // Validaciones exhaustivas antes de proceder
    const errores = validarParametrosCalculo(tipoPlanilla, periodo, empleadosSeleccionados, config);

    // Validación adicional para liquidación
    if (tipoPlanilla === 'LIQUIDACION' && !fechaCese) {
      errores.push('Debe especificar la fecha de cese para liquidación');
    }

    if (mostrarErroresValidacion(errores)) {
      return; // Detener ejecución si hay errores
    }

    // Validar que existan regímenes configurados
    if (!config.regimenes || typeof config.regimenes !== 'object') {
      mostrarErroresValidacion(['Regímenes laborales no configurados']);
      return;
    }

    console.log("✅ Validaciones pasadas, iniciando cálculo de planilla", {
      tipoPlanilla,
      periodo,
      empleadosSeleccionados: empleadosSeleccionados.length,
      regimenEmpresa,
      usuarioRuc: usuario?.ruc
    });

    if (!usuario?.ruc) {
      console.error("❌ Error: No se encontró el RUC del usuario en el componente ProcesoPlanilla");
    }

    setCalculando(true);
    setResultados(null);

    try {
      const ultimoDia = getUltimoDiaMes(periodo);
      const periodoCalculo = { fechaInicio: `${periodo}-01`, fechaFin: `${periodo}-${ultimoDia}` };
      const detalleCalculado = [];
      for (const empleadoId of empleadosSeleccionados) {
        const empleado = empleados.find((e) => e.id === empleadoId);
        const datosAsistencia = await obtenerDatosAsistencia(empleadoId, periodo, usuario, empleado);
        const regimenAplicar = regimenEmpresa;
        const configuracionRegimen = { regimen_principal: regimenAplicar, parametros: config.regimenes };
        let detalleEmpleado;
        if (tipoPlanilla === "GRATIFICACION") {
          detalleEmpleado = await calcularPlanillaGratificacion(empleado, empleadoId, periodo, ultimoDia, regimenAplicar, configuracionRegimen, datosAsistencia, config, bonosMap, calcularMesesComputables, calcularGratificacion, calcularSistemaPensionario, calcularRenta5ta, mostrarAlerta, usuario);
        } else if (tipoPlanilla === "CTS") {
          detalleEmpleado = await calcularPlanillaCTS(empleado, empleadoId, periodo, ultimoDia, regimenAplicar, configuracionRegimen, datosAsistencia, config, bonosMap, calcularMesesComputables, calcularCTS, mostrarAlerta, usuario);
        } else if (tipoPlanilla === "LIQUIDACION") {
          // Para liquidación, solo procesar un empleado a la vez
          if (empleadosSeleccionados.length > 1) {
            mostrarAlerta('Información', 'Para liquidación, seleccione solo un empleado a la vez', 'warning');
            return;
          }

          // Obtener historial para renta de 5ta categoría
          // Solo si es liquidación, necesitamos los acumulados del año
          let historial5ta = {
            ingresosAfectosPasados: 0,
            pagosCuenta5ta: 0,
            gratificacionesPercibidas: 0
          };

          try {
            const anioCese = fechaCese.split('-')[0];
            const periodoFin = periodoCalculo; // Mes de cese (excluyente para el query si es <, pero queremos < periodoCalculo)

            // Query para obtener todas las planillas del año hasta antes del cese
            // Collection: pl_planilla_detalle
            // Filtros: empleado_id, ruc_empresa, periodo >= anio-01, periodo < mes_cese
            console.log("🔍 Buscando historial 5ta:", {
              empleado_id: empleadoId,
              ruc: usuario.ruc,
              anio: anioCese,
              periodoFin
            });

            const qHistorial = query(
              collection(db, "pl_planilla_detalle"),
              where("empleado_id", "==", empleadoId),
              where("ruc_empresa", "==", usuario.ruc),
              where("periodo", ">=", `${anioCese}-01`),
              where("periodo", "<", periodoFin)
            );

            const snapshotHistorial = await getDocs(qHistorial);
            console.log(`🔍 Historial encontrado: ${snapshotHistorial.size} documentos`);

            snapshotHistorial.forEach(doc => {
              const data = doc.data();
              // Acumular Pagos a Cuenta (Retenciones ya hechas)
              historial5ta.pagosCuenta5ta += Number(data.descuentos?.renta_5ta || 0);

              // Acumular Ingresos Afectos Pasados
              historial5ta.ingresosAfectosPasados += Number(data.total_ingresos_afectos || 0);

              // Acumular Gratificaciones Percibidas (si existen en el detalle)
              // Normalmente la gratificación está en una columna 'gratificacion' o similar
              historial5ta.gratificacionesPercibidas += Number(data.gratificacion || 0);
            });

            console.log("🔍 Historial 5ta Acumulado:", historial5ta);

          } catch (error) {
            console.warn("⚠️ Error obteniendo historial 5ta:", error);
            // Continuar con valores en 0
          }

          detalleEmpleado = await calcularPlanillaLiquidacion(
            empleado,
            empleadoId,
            fechaCese,
            tipoCese,
            regimenAplicar,
            configuracionRegimen,
            config,
            bonosMap,
            calcularLiquidacion,
            usuario,
            historial5ta // Pasar historial
          );
        } else {
          console.log('🔍 Debug antes de calcularPlanillaMensual:', {
            regimenAplicar,
            configuracionRegimen: configuracionRegimen?.parametros?.GENERAL
          });
          detalleEmpleado = await calcularPlanillaMensual(
            empleado, 
            empleadoId, 
            periodoCalculo, 
            regimenAplicar, 
            configuracionRegimen, 
            datosAsistencia, 
            config, 
            bonosMap, 
            calcularDescuentosPorFaltas, 
            calcularDescuentosPorTardanzas, 
            calcularEssalud, 
            calcularRenta5ta, 
            calcularSistemaPensionario, 
            calcularEssaludVida, 
            calcularSCTR, 
            calcularVidaLey, 
            calcularBonificacionesConstruccionCivil, 
            usuario,
            ajustesData[empleadoId] || {}
          );
        }
        console.log(`👤 [ProcesoPlanilla] Detalle calculado para ${empleado.nombres}:`, {
          total_ingresos: detalleEmpleado.total_ingresos,
          total_descuentos: detalleEmpleado.total_descuentos,
          adelantos_monto: detalleEmpleado.descuentos?.adelantos,
          adelantos_count: detalleEmpleado.adelantos?.length
        });
        detalleCalculado.push(detalleEmpleado);
      }
      const totales = detalleCalculado.reduce((acc, curr) => ({
        ingresos: Number(acc.ingresos) + Number(curr.total_ingresos),
        descuentos: Number(acc.descuentos) + Number(curr.total_descuentos),
        aportaciones: Number(acc.aportaciones) + Number(curr.total_aportaciones),
        provisiones: Number(acc.provisiones) + (Number(curr.provisiones?.total) || 0),
        neto: Number(acc.neto) + Number(curr.neto_pagar),
        costo_laboral: Number(acc.costo_laboral) + (Number(curr.costo_laboral_total) || 0),
      }), { ingresos: 0, descuentos: 0, aportaciones: 0, provisiones: 0, neto: 0, costo_laboral: 0 });
      const resultadosCalculados = {
        periodo,
        tipo: tipoPlanilla,
        fecha_inicio: `${periodo}-01`,
        fecha_fin: `${periodo}-${ultimoDia}`,
        total_empleados: empleadosSeleccionados.length,
        total_ingresos: Number(totales.ingresos.toFixed(2)),
        total_descuentos: Number(totales.descuentos.toFixed(2)),
        total_aportaciones: Number(totales.aportaciones.toFixed(2)),
        total_provisiones: Number(totales.provisiones.toFixed(2)),
        total_neto: Number(totales.neto.toFixed(2)),
        costo_laboral_total: Number(totales.costo_laboral.toFixed(2)),
        detalle: detalleCalculado,
      };
      setResultados(resultadosCalculados);

      console.log("✅ Cálculo de planilla completado exitosamente", {
        empleados: resultadosCalculados.total_empleados,
        totalNeto: resultadosCalculados.total_neto
      });

    } catch (error) {
      console.error("❌ Error crítico al calcular planilla:", error);

      // Manejo específico de tipos de error
      let mensajeError = "Error en el cálculo de la planilla";

      if (error.message?.includes('permission-denied')) {
        mensajeError = "No tiene permisos para acceder a los datos";
      } else if (error.message?.includes('not-found')) {
        mensajeError = "No se encontraron datos de empleados o configuración";
      } else if (error.message?.includes('invalid-argument')) {
        mensajeError = "Datos inválidos en el cálculo";
      } else if (error.name === 'TypeError') {
        mensajeError = "Error de tipo de datos en el cálculo";
      } else if (error.name === 'RangeError') {
        mensajeError = "Valor fuera de rango en el cálculo";
      }

      // Mostrar error detallado al usuario
      Swal.fire({
        icon: 'error',
        title: 'Error en el Cálculo',
        html: `<div style="text-align: left;">
          <strong>${mensajeError}</strong><br>
          <small style="color: #666;">Detalles técnicos: ${error.message}</small>
        </div>`,
        confirmButtonText: 'Entendido'
      });

      // Limpiar resultados en caso de error
      setResultados(null);
    } finally {
      setCalculando(false);
    }
  };

  const guardarPlanilla = async () => {
    if (!resultados) return;
    setLoading(true);
    try {
      const planillaId = `${usuario.ruc}_${resultados.periodo}`;
      const ultimoDia = getUltimoDiaMes(resultados.periodo);
      await setDoc(doc(db, "pl_planillas", planillaId), {
        ruc_empresa: usuario.ruc,
        periodo: resultados.periodo,
        tipo_planilla: resultados.tipo,
        regimen_empresa: regimenEmpresa,
        config: config, // <--- NUEVO: Guardar configuración para trazabilidad y exportación
        fecha_inicio: `${resultados.periodo}-01`,
        fecha_fin: `${resultados.periodo}-${ultimoDia}`,
        total_empleados: resultados.total_empleados,
        total_ingresos: resultados.total_ingresos,
        total_descuentos: resultados.total_descuentos,
        total_aportaciones: resultados.total_aportaciones,
        total_provisiones: resultados.total_provisiones || 0,
        total_neto: resultados.total_neto,
        costo_laboral_total: resultados.costo_laboral_total || 0,
        estado: "BORRADOR",
        created_at: new Date(),
        updated_at: new Date(),
      });
      for (const detalle of resultados.detalle) {
        await setDoc(doc(db, "pl_planilla_detalle", `${planillaId}_${detalle.empleado_id}`), {
          ...detalle,
          planilla_id: planillaId,
          ruc_empresa: usuario.ruc,
          periodo: resultados.periodo,
          created_at: new Date(),
          updated_at: new Date(),
        });
      }

      // Marcar adelantos como aplicados
      const adelantosIds = [];
      for (const detalle of resultados.detalle) {
        if (detalle.adelantos && detalle.adelantos.length > 0) {
          adelantosIds.push(...detalle.adelantos.map(a => a.id));
        }
      }
      if (adelantosIds.length > 0) {
        await marcarAdelantosComoAplicados(adelantosIds, usuario);
      }

      // Recalcular retenciones acumuladas en el año a partir de los detalles guardados en pl_planilla_detalle
      const anioActual = resultados.periodo.split("-")[0];
      for (const detalle of resultados.detalle) {
        try {
          const empId = detalle.empleado_id;
          const qDetallesAnio = query(
            collection(db, "pl_planilla_detalle"),
            where("empleado_id", "==", empId),
            where("ruc_empresa", "==", usuario.ruc),
            where("periodo", ">=", `${anioActual}-01`),
            where("periodo", "<=", `${anioActual}-12`)
          );
          const snapDetalles = await getDocs(qDetallesAnio);
          let totalRetencionesAnio = 0;
          snapDetalles.forEach((dDoc) => {
            totalRetencionesAnio += Number(dDoc.data().descuentos?.renta_5ta || 0);
          });
          const empleadoRef = doc(db, "pl_empleados", empId);
          await updateDoc(empleadoRef, {
            retenciones_acumuladas: totalRetencionesAnio,
            updated_at: new Date(),
          });
        } catch (eRet) {
          console.warn(`Error al actualizar retenciones acumuladas para ${detalle.empleado_id}:`, eRet);
        }
      }

      // AUDITORÍA GENERACIÓN / EDICIÓN
      try {
        const accionAuditoria = id ? "EDICION_PLANILLA" : "GENERACION_PLANILLA";
        await logBusinessAction(accionAuditoria, "GestionPlanillas", {
          periodo: resultados.periodo,
          tipoPlanilla: resultados.tipo,
          totalEmpleados: resultados.total_empleados,
          totalNeto: resultados.total_neto
        }, usuario);
      } catch (e) {
        console.warn(`No se pudo registrar la auditoría de ${id ? "edición" : "generación"} de planilla`, e);
      }

      Swal.fire({ icon: "success", title: "Planilla guardada", text: "La planilla se guardó correctamente y las retenciones se acumularon" });
      navigate("/planillas");
    } catch (error) {
      console.error("Error al guardar:", error);
      mostrarError("Error al guardar la planilla");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="px-4 py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header Premium */}
      <div className="mb-4 p-4 rounded-3 shadow-sm border-0" style={{
        background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
        color: "white"
      }}>
        <Row className="align-items-center">
          <Col>
            <div className="d-flex align-items-center">
              <div className="p-3 rounded-circle bg-white bg-opacity-25 me-3">
                <FaCalculator size={24} />
              </div>
              <div>
                <h3 className="mb-0 fw-bold">{id ? "Ver Planilla" : "Procesar Planilla"}</h3>
                <p className="mb-0 opacity-75">Configuración y cálculo detallado de compensaciones</p>
              </div>
            </div>
          </Col>
          <Col xs="auto">
            <Button
              variant="light"
              className="rounded-pill px-4 shadow-sm border-0 fw-bold"
              style={{ color: "#8B4513" }}
              onClick={() => navigate("/planillas")}
            >
              <FaArrowLeft className="me-2" /> Volver
            </Button>
          </Col>
        </Row>
      </div>

      {!resultados && (
        <Row>
          {/* Configuración */}
          <Col lg={4} className="mb-4">
            <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden">
              <Card.Header className="bg-white py-3 border-0">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
                  <FaDatabase className="me-2 text-warning" /> Configuración General
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold text-muted small text-uppercase">Periodo de Pago</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-end-0">
                      <FaCalendarAlt className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      type="month"
                      className="bg-light border-start-0 ps-0"
                      value={periodo}
                      onChange={(e) => setPeriodo(e.target.value)}
                      disabled={!!id}
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold text-muted small text-uppercase">Tipo de Planilla</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-end-0">
                      <FaMoneyBillWave className="text-muted" />
                    </InputGroup.Text>
                    <Form.Select
                      className="bg-light border-start-0 ps-0 text-dark"
                      value={tipoPlanilla}
                      onChange={(e) => setTipoPlanilla(e.target.value)}
                      disabled={!!id}
                      style={{ fontWeight: "500" }}
                    >
                      <option value="MENSUAL">Mensual</option>
                      <option value="GRATIFICACION">Gratificación</option>
                      <option value="CTS">CTS</option>
                      <option value="LIQUIDACION">Liquidación</option>
                    </Form.Select>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold text-muted small text-uppercase">Régimen Principal</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-end-0">
                      <FaBriefcase className="text-muted" />
                    </InputGroup.Text>
                    <Form.Select
                      className="bg-light border-start-0 ps-0"
                      value={regimenEmpresa}
                      onChange={(e) => setRegimenEmpresa(e.target.value)}
                      disabled={!!id}
                    >
                      <option value="GENERAL">General (D.Leg. 728)</option>
                      <option value="MYPE_PEQUENA">Pequeña empresa (REMYPE)</option>
                      <option value="MYPE_MICRO">Microempresa (REMYPE)</option>
                      <option value="AGRARIO">Régimen Agrario (Ley 31110)</option>
                      <option value="CONSTRUCCION_CIVIL">Construcción Civil</option>
                      <option value="CAS">CAS (D.Leg. 1057)</option>
                    </Form.Select>
                  </InputGroup>
                </Form.Group>

                {tipoPlanilla === "LIQUIDACION" && (
                  <div className="p-3 rounded-4 bg-light mb-3 border border-warning border-opacity-25">
                    <h6 className="fw-bold text-warning mb-3">Datos de Cese</h6>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold text-muted small text-uppercase">Fecha de Cese</Form.Label>
                      <Form.Control
                        type="date"
                        value={fechaCese}
                        onChange={(e) => setFechaCese(e.target.value)}
                        disabled={!!id}
                      />
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label className="fw-bold text-muted small text-uppercase">Motivo del Cese</Form.Label>
                      <Form.Select value={tipoCese} onChange={(e) => setTipoCese(e.target.value)} disabled={!!id}>
                        <option value="despido">Despido</option>
                        <option value="renuncia">Renuncia</option>
                        <option value="mutuo_acuerdo">Mutuo Acuerdo</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                )}

                <Button
                  variant="primary"
                  className="w-100 py-3 rounded-4 border-0 shadow-sm mt-3"
                  style={{ background: "#8B4513", fontWeight: "600" }}
                  onClick={calcularPlanilla}
                  disabled={calculando || !periodo || empleadosSeleccionados.length === 0 || (tipoPlanilla === "LIQUIDACION" && !fechaCese)}
                >
                  {calculando ? (
                    <><Spinner animation="border" size="sm" className="me-2" /> Calculando...</>
                  ) : (
                    <><FaCalculator className="me-2" /> Calcular Planilla</>
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>

          {/* Selección de Empleados o Resultados */}
          <Col lg={8}>

            <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100">
              <Card.Header className="bg-white py-3 border-0">
                <Row className="align-items-center">
                  <Col>
                    <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
                      <FaUsers className="me-2 text-info" /> Selección de Empleados
                      <Badge bg="light" text="dark" className="ms-2 border">{empleadosSeleccionados.length}</Badge>
                    </h5>
                  </Col>
                  <Col md={4}>
                    <InputGroup size="sm">
                      <InputGroup.Text className="bg-light border-0"><FaSearch className="text-muted" /></InputGroup.Text>
                      <Form.Control
                        placeholder="Buscar por nombre o DNI"
                        className="bg-light border-0"
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col xs="auto">
                    <Button variant="outline-dark" size="sm" className="rounded-pill px-3" onClick={toggleTodos}>
                      {empleadosSeleccionados.length === empleados.length ? "Deseleccionar" : "Seleccionar Todos"}
                    </Button>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive" style={{ maxHeight: "calc(100vh - 400px)", overflowY: "auto" }}>
                  <Table hover className="align-middle mb-0">
                    <thead className="bg-light" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                      <tr className="text-muted small text-uppercase">
                        <th className="px-4 py-3" style={{ width: "40px" }}></th>
                        <th className="py-3">Empleado</th>
                        <th className="py-3">Cargo / Régimen</th>
                        <th className="py-3 text-end px-4">Sueldo Base</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empleados
                        .filter(e =>
                          (e.nombres + " " + e.apellidos).toLowerCase().includes(searchTerm.toLowerCase()) ||
                          e.dni.includes(searchTerm)
                        )
                        .map((empleado) => (
                          <tr
                            key={empleado.id}
                            onClick={() => toggleEmpleado(empleado.id)}
                            style={{ cursor: "pointer", transition: "all 0.2s" }}
                            className={empleadosSeleccionados.includes(empleado.id) ? "bg-light-yellow" : ""}
                          >
                            <td className="px-4">
                              <Form.Check
                                type="checkbox"
                                checked={empleadosSeleccionados.includes(empleado.id)}
                                onChange={() => { }} // Se maneja con el tr onClick
                              />
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="p-2 rounded bg-light me-3 fw-bold text-muted small">
                                  {empleado.nombres.charAt(0)}{empleado.apellidos.charAt(0)}
                                </div>
                                <div>
                                  <div className="fw-bold text-dark">{empleado.nombres} {empleado.apellidos}</div>
                                  <div className="text-muted small">{empleado.dni}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="text-dark small fw-medium">{empleado.cargo}</div>
                              <Badge bg="light" text="dark" className="border small font-monospace">{empleado.regimen_pensionario}</Badge>
                            </td>
                            <td className="text-end px-4 fw-bold text-dark">
                              S/ {empleado.sueldo_base?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

          </Col>
        </Row>
      )}

      {resultados && (
        <Row className="mt-4">
          <Col lg={12}>
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100">
              <Card.Header className="bg-white py-3 border-0">
                <Row className="align-items-center">
                  <Col>
                    <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
                      <FaChartLine className="me-2 text-success" /> Resumen de Planilla
                    </h5>
                  </Col>
                  <Col xs="auto">
                    <Button variant="outline-info" size="sm" className="rounded-pill px-3 me-2" onClick={() => setResultados(null)}>
                      <FaSync className="me-1" /> Reintentar
                    </Button>
                    <Button variant="success" size="sm" className="rounded-pill px-3 me-2" onClick={guardarPlanilla} disabled={loading}>
                      <FaSave className="me-1" /> {loading ? "Guardando..." : "Guardar"}
                    </Button>
                    <Button variant="outline-success" size="sm" className="rounded-pill px-3" onClick={() => exportarAExcel(resultados, tipoPlanilla, config)}>
                      <FaFileExcel className="me-1" /> Excel
                    </Button>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-4 bg-light bg-opacity-50">
                <Row className="g-3 mb-4">
                  {[
                    { label: "Neto a Pagar", value: resultados.total_neto, color: "#8B4513", icon: <FaMoneyBillWave /> },
                    { label: "Total Ingresos", value: resultados.total_ingresos, color: "#2E8B57", icon: <FaPlus /> },
                    { label: "Total Descuentos", value: resultados.total_descuentos, color: "#CD5C5C", icon: <FaTimes /> },
                    { label: "Costo Laboral", value: resultados.costo_laboral_total, color: "#4682B4", icon: <FaChartBar /> },
                  ].map((stat, i) => (
                    <Col md={6} xl={3} key={i}>
                      <div className="p-3 bg-white rounded-4 shadow-sm border-start border-4 h-100" style={{ borderStartColor: stat.color }}>
                        <div className="text-muted small fw-bold mb-1 text-uppercase">{stat.label}</div>
                        <div className="h4 mb-0 fw-bold" style={{ color: stat.color }}>S/ {stat.value?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <div className="bg-white rounded-4 shadow-sm overflow-hidden p-0">
                  <PlanillaTable 
                    resultados={resultados} 
                    tipoPlanilla={tipoPlanilla} 
                    regimenEmpresa={regimenEmpresa} 
                    config={config}
                    onAbrirAjustes={handleAbrirAjustes}
                  />

                  {showModalAjustes && (
                    <ModalAjustesManuales
                      show={showModalAjustes}
                      onHide={() => setShowModalAjustes(false)}
                      empleado={empleadoSeleccionadoAjustes}
                      ajustesIniciales={empleadoSeleccionadoAjustes ? ajustesData[empleadoSeleccionadoAjustes.empleado_id] : null}
                      onGuardar={handleGuardarAjustes}
                    />
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Resumen de Asistencia - Solo si hay incidencias */}
      {resultados?.detalle?.some((d) =>
        d.faltas_injustificadas > 0 ||
        d.faltas_justificadas > 0 ||
        d.dias_tardanzas > 0 ||
        d.permisos_sin_goce > 0 ||
        d.permisos_con_goce > 0
      ) && (
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden mt-4 mb-4">
            <Card.Header className="bg-warning text-dark py-3 border-0">
              <h6 className="mb-0 fw-bold d-flex align-items-center">
                <FaClipboardList className="me-2" /> 📊 Análisis de Incidencias y Asistencia
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="align-middle mb-0">
                <thead className="bg-light">
                  <tr className="text-muted small text-uppercase">
                    <th className="px-4 py-3">Empleado</th>
                    <th className="text-center">Faltas (I/J)</th>
                    <th className="text-center">Permisos (S/C Goce)</th>
                    <th className="text-center">Tardanzas</th>
                    <th className="text-end">Desc. Faltas</th>
                    <th className="text-end">Desc. DSO</th>
                    <th className="text-end">Desc. Permisos S/Goce</th>
                    <th className="text-end">Desc. Tardanzas</th>
                    <th className="text-end px-4">Total Descuento</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.detalle
                    .filter((detalle) =>
                      detalle.faltas_injustificadas > 0 ||
                      detalle.faltas_justificadas > 0 ||
                      detalle.dias_tardanzas > 0 ||
                      detalle.permisos_sin_goce > 0 ||
                      detalle.permisos_con_goce > 0
                    )
                    .map((detalle, index) => (
                      <tr key={index}>
                        <td className="px-4 fw-bold">{detalle.nombres}</td>
                        <td className="text-center">
                          <Badge bg={detalle.faltas_injustificadas > 0 ? "danger" : "light"} text={detalle.faltas_injustificadas > 0 ? "white" : "dark"} className="rounded-pill px-2 me-1">
                            {detalle.faltas_injustificadas || 0}
                          </Badge>
                          <Badge bg="info" text="white" className="rounded-pill px-2">
                            {detalle.faltas_justificadas || 0}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg={detalle.permisos_sin_goce > 0 ? "warning" : "light"} text="dark" className="rounded-pill px-2 me-1">
                            {detalle.permisos_sin_goce || 0}
                          </Badge>
                          <Badge bg="success" text="white" className="rounded-pill px-2">
                            {detalle.permisos_con_goce || 0}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg={detalle.dias_tardanzas > 0 ? "info" : "light"} text="white" className="rounded-pill px-2 text-dark">
                            {detalle.dias_tardanzas || 0} d / {detalle.minutos_tardanza_total || 0} m
                          </Badge>
                        </td>
                        <td className="text-end text-danger fw-medium">S/ {detalle.descuentos?.descuento_faltas?.toFixed(2)}</td>
                        <td className="text-end text-danger fw-medium">S/ {detalle.descuentos?.descuento_dso?.toFixed(2)}</td>
                        <td className="text-end text-danger fw-medium">S/ {Number(detalle.descuentos?.descuento_permisos_sin_goce || 0).toFixed(2)}</td>
                        <td className="text-end text-danger fw-medium">S/ {detalle.descuentos?.descuento_tardanzas?.toFixed(2)}</td>
                        <td className="text-end px-4 fw-bold text-danger">
                          S/ {(
                            Number(detalle.descuentos?.descuento_faltas || 0) +
                            Number(detalle.descuentos?.descuento_dso || 0) +
                            Number(detalle.descuentos?.descuento_permisos_sin_goce || 0) +
                            Number(detalle.descuentos?.descuento_tardanzas || 0)
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

      {/* Glosa de Regímenes */}
      {resultados && (
        <Card className="border-0 bg-secondary bg-opacity-10 rounded-4 mt-4">
          <Card.Body className="p-3 text-muted small d-flex align-items-start font-monospace">
            <FaInfoCircle className="me-2 mt-1 flex-shrink-0" />
            <div>
              <span className="fw-bold">Régimen aplicado:</span> {getDescripcionRegimen(regimenEmpresa)}
              {regimenEmpresa === "MYPE_MICRO" && (
                <div className="mt-1 text-info">
                  * Este régimen no incluye Gratificaciones ni CTS, y el descanso vacacional es de 15 días.
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default ProcesoPlanilla;
